import { loadSupersetFilters } from '../charts/superset-bundle.js';
import { fromDataMask, toPickerState } from '../filters/date-filter-value.js';
import { createDateFilterKeyboard } from '../filters/date-filter-keyboard.js';

/**
 * The Alpine half of <x-filters.date-range> and <x-filters.date>
 * (App\View\Components\Filters\DateFilter): Superset's own picker from the blink-superset bundle,
 * bound to a time_range through x-modelable: a Livewire property (wire:model) or an Alpine one
 * (x-model).
 *
 * df_value IS the bound property: its owner moves it when the server or the page does, and the host
 * moves it when the reader picks. date-filter-value.js is the contract between the two;
 * date-filter-keyboard.js is what the picker lacks for a keyboard and a screen reader.
 * blink-superset-bundle.md, "The x-filters hosts", says why each rule below exists.
 */

const EMPTY_PRESETS = { enabled: null, order: [], ranges: {} };

/** The preset column and PHP's dates, re-rendered by the server on every Livewire render. */
function readPresets(root) {
    const node = root.querySelector('script[data-date-filter-presets]');
    return node ? { ...EMPTY_PRESETS, ...JSON.parse(node.textContent || '{}') } : EMPTY_PRESETS;
}

export default function DateFilter(config = {}) {
    const kind = config.kind === 'date' ? 'date' : 'dateRange';
    const text = config.text ?? {};

    // In the closure, not reactive: Alpine would deep-proxy the bundle and the React root it drives.
    let bundle = null;
    let keyboard = null;
    let observers = [];
    // Watching a host that has no layout box yet, until it is shown (df_loadWhenShown).
    let shown = null;
    let alive = true;
    let mounted = false;
    // The preset the host mounted with or last wrote: the preset buttons' aria-pressed. The component
    // draws its own highlight from the dates it is given, matched against its browser-side presets,
    // so the two can differ (blink-superset-bundle.md, "Known gaps").
    let shownPresetKey = null;
    // The value this host last wrote, until $watch hands it back.
    let sent;
    let hasSent = false;
    // The bound value the picker shows, so a $watch for it is not taken for a change.
    let mountedFor;
    // A preset label the reader pressed, until the component answers it (or does not).
    let pressed = null;

    return {
        df_value: null,
        df_state: 'loading',
        df_announcement: '',
        // The filters chunk did not download, as against a mount that threw: the browser keeps a
        // module that failed to fetch for the rest of the page, so only a reload brings it back.
        df_needsReload: false,
        df_text: {
            startDate: text.startDate ?? 'start date',
            endDate: text.endDate ?? 'end date',
            clear: text.clear ?? 'Clear dates',
            loadFailed: text.loadFailed ?? 'This date filter could not be loaded.',
            retry: text.retry ?? 'Retry',
            reload: text.reload ?? 'Reload page',
        },

        init() {
            // Two hosts with one id share their label, hint and error ids, so each would name the
            // other's inputs; it happens whenever two components bind one name.
            const twins = document.querySelectorAll(`[data-date-filter="${config.id}"]`);
            if (twins.length > 1 && twins[0] !== this.$root) {
                console.error('Date filter id is not unique on the page: pass a distinct id', { id: config.id });
            }

            keyboard = createDateFilterKeyboard({
                root: this.$root,
                mount: this.$refs.mount,
                popup: this.$refs.popup,
                kind,
                ids: config.ids ?? {},
                presetOrder: () => readPresets(this.$root).order,
                text: this.df_text,
                popupContainer: config.popupContainer ?? null,
                required: config.required === true,
                currentPresetKey: () => shownPresetKey,
                onPresetPress: (key) => this.df_presetPressed(key),
            });

            this.$watch('df_value', (value) => this.df_follow(value));

            // Both are server-rendered on the morphable root, while the picker they describe is
            // React's: a validation error, and the preset column and dates after a render.
            const invalid = new MutationObserver(() => this.df_mount(this.df_value));
            invalid.observe(this.$root, { attributes: true, attributeFilter: ['data-invalid'] });
            observers = [invalid];

            const presets = this.$root.querySelector('script[data-date-filter-presets]');
            if (presets) {
                const moved = new MutationObserver(() => this.df_mount(this.df_value));
                moved.observe(presets, { childList: true, characterData: true, subtree: true });
                observers.push(moved);
            }

            // A microtask later: x-modelable hands over the bound value in a microtask it queues
            // after init(), and a chunk already fetched would otherwise mount an empty picker first.
            queueMicrotask(() => this.df_loadWhenShown());
        },

        /**
         * A host with no layout box (in a closed drawer or dialog) waits until it is shown before it
         * fetches the filters chunk, about 430 KB gzipped of antd, so a page does not pay for a picker
         * nobody opened. Shown means displayed, not scrolled into view: a host below the fold still
         * loads, or a keyboard user could never Tab to its inputs.
         */
        df_loadWhenShown() {
            const hasBox = () => this.$root.getClientRects().length > 0;
            if (typeof ResizeObserver !== 'function' || typeof this.$root.getClientRects !== 'function' || hasBox()) {
                this.df_load();
                return;
            }

            shown = new ResizeObserver(() => {
                if (! alive || ! hasBox()) return;
                shown.disconnect();
                shown = null;
                this.df_load();
            });
            shown.observe(this.$root);
        },

        destroy() {
            alive = false;
            shown?.disconnect();
            observers.forEach((observer) => observer.disconnect());
            keyboard?.destroy();
            if (bundle && mounted) this.df_unmount();
            bundle = null;
        },

        df_load({ refocus = false } = {}) {
            if (! alive) return;
            this.df_state = 'loading';

            loadSupersetFilters()
                .then((loaded) => {
                    if (! alive) return;
                    bundle = loaded;
                    this.df_state = 'ready';
                    if (! this.df_mount(this.df_value, { fresh: true, refocus })) return;
                    // A recovered filter must not leave "could not be loaded" behind in the alert.
                    this.df_announcement = '';
                    if (refocus) keyboard.focusTrigger();
                }, (error) => {
                    // The chunk never arrived. A page open while the app's assets were rebuilt (a
                    // deploy, or another build into the same public/build) asks for a file name that
                    // is gone, and the browser answers every later import of it with the same failure
                    // without a request, so pressing Retry could never bring the filter back.
                    this.df_needsReload = true;
                    this.df_fail(error, refocus);
                })
                .catch((error) => this.df_fail(error, refocus));
        },

        /**
         * A mount that threw is mounted again from the bundle already held. A chunk that did not
         * download needs the page loaded again, the one recovery, which the button then says it does,
         * as a chart host's does (superset-chart.js, sc_retry).
         */
        df_retry() {
            if (this.df_needsReload) {
                window.location.reload();
                return;
            }

            this.df_load({ refocus: true });
        },

        df_fail(error, refocus = false) {
            console.error('Date filter failed to load', { id: config.id, error });
            bundle = null;
            mounted = false;
            this.df_state = 'failed';
            // Cleared first and set on the next frame, so a second failure in a row is spoken again.
            this.df_announcement = '';
            requestAnimationFrame(() => { this.df_announcement = this.df_text.loadFailed; });
            // Pressing Retry hid the button; a failure brings it back, and focus with it.
            if (refocus) this.$nextTick(() => this.$refs.retry?.focus());
        },

        df_unmount() {
            (kind === 'date' ? bundle.unmountDatePickerFilter : bundle.unmountDateRangeFilter)(this.$refs.mount);
            mounted = false;
        },

        /**
         * Mounts, or re-renders in place, for a bound value. Returns whether the picker is up.
         * `refocus` is Retry's: a mount that throws again brings Retry back, and focus with it.
         */
        df_mount(value, { fresh = false, refocus = false } = {}) {
            if (! bundle) return false;

            const presets = readPresets(this.$root);
            const picker = toPickerState(value, { kind, presetRanges: presets.ranges });
            if (! picker.readable) console.error('Date filter value is not a time_range it can show', { id: config.id, value });

            const invalid = this.$root.dataset.invalid === 'true';
            const options = {
                id: config.id,
                filterState: { value: picker.value, validateStatus: invalid ? 'error' : undefined },
                onDataMask: (dataMask, change) => this.df_receive(dataMask, change),
                getPopupContainer: () => keyboard.popupContainer(),
            };

            try {
                if (fresh && mounted) this.df_unmount();

                if (kind === 'date') {
                    bundle.mountDatePickerFilter(this.$refs.mount, { ...options, formData: { singleDate: true } });
                } else {
                    bundle.mountDateRangeFilter(this.$refs.mount, {
                        ...options,
                        // Always the column the host names its rows by, even when it is the whole of
                        // it: left to the component's own order, one drifting from the enum would put
                        // every key the host reports on the wrong row.
                        formData: { enabledPresets: presets.enabled ?? presets.order },
                        presetKey: picker.presetKey,
                    });
                }
            } catch (error) {
                // The adapter registers its instance before it renders; left behind, Retry would
                // re-render into what the failed render left instead of mounting afresh.
                this.df_unmount();
                this.df_fail(error, refocus);
                return false;
            }

            if (! mounted || fresh) shownPresetKey = kind === 'dateRange' ? picker.presetKey : null;
            mounted = true;
            mountedFor = value;
            keyboard.wire({ invalid });

            return true;
        },

        /**
         * The keyboard layer saw a preset label pressed (by mouse or key) just before the component
         * handles it. The component answers with a DataMask — or with nothing: re-picking the
         * highlighted rolling preset changes no date, and All Time on an empty picker changes
         * nothing either. Unanswered, the press is still the reader's pick, and the fresh mount
         * also drops the key the component kept in a ref, which would otherwise arm the next range
         * typed in to roll over at midnight as that preset.
         */
        df_presetPressed(key) {
            // A row the host cannot name: nothing to write, and null would throw the dates away.
            if (key === null) return;
            pressed = key;
            setTimeout(() => {
                if (pressed !== key) return;
                pressed = null;
                if (! alive || ! bundle) return;
                this.df_write(key);
                this.df_mount(key, { fresh: true });
                keyboard.returnFocus();
            }, 0);
        },

        df_write(value) {
            sent = value;
            hasSent = true;
            this.df_value = value;
        },

        df_receive(dataMask, change) {
            // A key is believed only for a press the host saw too: the adapter reports the row a
            // click landed on, the keyboard layer the row it passed on, and a key only one of them
            // vouches for is not written.
            const key = kind === 'dateRange' && change?.presetKey ? change.presetKey : null;
            const trusted = key !== null && key === pressed ? key : null;
            if (! change?.auto) pressed = null;

            const next = fromDataMask(dataMask, { ...change, presetKey: trusted }, { kind });
            if (! next.write) return;

            if (kind === 'dateRange') shownPresetKey = trusted;
            this.df_write(next.value);

            // After React's event: a render inside it would batch into the event's own.
            queueMicrotask(() => {
                if (! alive || ! bundle) return;

                if (key !== null) {
                    // A preset is shown with PHP's dates, in a fresh picker: after a keyboard pick
                    // antd keeps half of a two-field selection, and the next range typed into that
                    // instance submits on its first Enter (measured). A fresh mount also drops the
                    // preset the component armed to roll over at midnight.
                    this.df_mount(next.value, { fresh: true });
                } else {
                    // A typed or calendar range, or a clear: the picker already shows it. Mounted in
                    // place so the value it holds matches what it shows, for the next change to land.
                    this.df_mount(next.shown ?? null);
                    mountedFor = next.value;
                }

                keyboard.returnFocus();
            });
        },

        df_follow(value) {
            const own = hasSent && value === sent;
            hasSent = false;
            sent = undefined;
            if (own || ! bundle || (mounted && value === mountedFor)) return;

            const picker = toPickerState(value, { kind, presetRanges: readPresets(this.$root).ranges });
            const fresh = kind === 'dateRange' && picker.presetKey !== shownPresetKey;
            // A fresh mount removes the input the reader is on when the page answers their own write
            // with another value (a clear rewritten to a default preset), which would drop them on
            // <body>. A change made while they were elsewhere leaves their focus where it is.
            const hadFocus = fresh && keyboard.hasFocus();
            this.df_mount(value, { fresh });
            if (hadFocus) keyboard.focusTrigger();
        },
    };
}
